import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { useNavigate } from 'react-router-dom';
import { TaskDetailDialog } from 'shared/dialogs/task-detail';
import { useActivePath } from 'shared/hooks/use-active-path';
import { useKeysDown } from 'shared/hooks/use-keys-down';
import { useModifierKeysDown } from 'shared/hooks/use-modifier-keys-down';
import { Paths } from 'shared/routes';
import { ID } from 'shared/types/id';
import { CreateTask, Task } from 'shared/types/task';
import { TaskActionType } from 'shared/types/task-action';
import { TaskType } from 'shared/types/task-base';
import { replaceUrlParams } from 'shared/utils/replace-url-params';
import { targetIsInput } from 'shared/utils/target-is-input';
import { MetaViewOptions } from 'shared/views/task-detail';
import { useTheme } from 'styled-components';

import * as Styled from './task-detail.style';
import {
  TaskDetailContext,
  TaskDetailContextProps,
} from './task-detail-context';

export type TaskDetailProviderProps = {
  children: React.ReactNode;
};

export const TaskDetailProvider: React.FC<TaskDetailProviderProps> = ({
  children,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const activePath = useActivePath({ level: 2 });
  const { id: activePathId } = useParams();

  const closeTimerRef = useRef<number>();
  const [initialValues, setInitialValues] = useState<
    Partial<CreateTask> | undefined
  >(undefined);
  const [activeTask, setActiveTask] = useState<ID>();
  const [initialView, setInitialView] = useState(MetaViewOptions.Settings);

  const openTask = useCallback(
    (task: Task | undefined, view?: MetaViewOptions) => {
      if (task?.action?.type === TaskActionType.GoalTimeslot) {
        navigate(
          replaceUrlParams(Paths.GoalDetail, { id: task.action.meta.id }),
        );
        return;
      }

      if (task?.action?.type === TaskActionType.LifeAreaTimeslot) {
        navigate(
          replaceUrlParams(Paths.LifeAreaDetail, { id: task.action.meta.id }),
        );
        return;
      }

      closeTimerRef.current && window.clearTimeout(closeTimerRef.current);
      setActiveTask(task?.id);
      setInitialView(
        view ??
          (task?.type === TaskType.Habit
            ? MetaViewOptions.Statistics
            : MetaViewOptions.Settings),
      );
      setInitialValues(undefined);

      if (theme.isMobile) {
        document.body.style.overflow = task ? 'hidden' : '';
      }
    },
    [navigate, theme.isMobile],
  );

  const openTaskStatistics = useCallback(
    (task: Task | undefined) => {
      if (task?.action?.type === TaskActionType.GoalTimeslot) {
        navigate(
          replaceUrlParams(Paths.GoalDetail, { id: task.action.meta.id }),
        );
        return;
      }

      if (task?.action?.type === TaskActionType.LifeAreaTimeslot) {
        navigate(
          replaceUrlParams(Paths.LifeAreaDetail, { id: task.action.meta.id }),
        );
        return;
      }

      closeTimerRef.current && window.clearTimeout(closeTimerRef.current);
      setActiveTask(task?.id);
      setInitialValues(undefined);
    },
    [navigate],
  );

  const openCreateTask = useCallback((initialValues?: Partial<CreateTask>) => {
    setInitialValues(initialValues ?? {});
    setActiveTask(undefined);
  }, []);

  const closeDetail = useCallback(() => {
    setActiveTask(undefined);
    setInitialValues(undefined);
    document.body.style.overflow = '';
  }, []);

  const openCreateFromShortcut = useCallback(() => {
    const initialProps: Partial<CreateTask> = {};

    switch (activePath) {
      // case Paths.Home:
      //   initialProps.endStrategy = { deadline: today, completionCount: null };
      //   break;
      case Paths.GoalDetail:
        initialProps.goalId = activePathId as ID;
        break;
      case Paths.LifeAreaDetail:
        initialProps.lifeAreaId = activePathId as ID;
        break;
      case Paths.TagDetail:
        initialProps.tagIds = [activePathId as ID];
        break;
    }

    openCreateTask(initialProps);
  }, [activePath, activePathId, openCreateTask]);

  const [isSubmenuOpen, setSubmenuOpen] = useState(false);

  const value = useMemo<TaskDetailContextProps>(
    () => ({
      openTaskDetail: openTask,
      openTaskStatistics: openTaskStatistics,
      openCreateTask,
      activeTaskId: activeTask,
      closeDetail,
      isSubmenuOpen,
      setSubmenuOpen,
    }),
    [
      activeTask,
      closeDetail,
      isSubmenuOpen,
      openCreateTask,
      openTask,
      openTaskStatistics,
    ],
  );

  useModifierKeysDown('KeyN', (e) => {
    if (!window.electron || targetIsInput(e.target)) {
      return; // Don't do a thing
    }

    openCreateFromShortcut();
    e.preventDefault();
  });

  useKeysDown(['KeyN'], (e) => {
    if (window.electron || targetIsInput(e.target)) {
      return; // Don't do a thing
    }

    openCreateFromShortcut();
    e.preventDefault();
  });

  return (
    <TaskDetailContext.Provider value={value}>
      <Styled.Container>
        {children}

        <TaskDetailDialog
          taskId={activeTask}
          onClose={closeDetail}
          initialValues={initialValues}
          openOnView={initialView}
        />
      </Styled.Container>
    </TaskDetailContext.Provider>
  );
};
